import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';

import { Clear as ClearIcon, Search } from '@mui/icons-material';
import { SwapVert as SwapVertIcon } from '@mui/icons-material';
import { Box, IconButton, InputAdornment, OutlinedInput, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CustomButton from 'components/Button';
import { FilterButtons, FilterPanelContainer, PageContainer } from 'components/GlobalStyles/styles';
import SnackBar, { MessageType } from 'components/SnackBar';
import PeriodSelector from 'features/PeriodSelector';
import AddNewTeam from 'pages/TeamDetail/Components/AddNewTeamModal';
import { useGetTeamsQuery, useGetUserListQuery, useResourceUtilizationQuery } from 'services/busmanApi';

import { RootState } from '../../store';
import TeamsOverview from '../TeamOverview';

import Tabs from './components/Tabs';
import TeamsView from './components/TeamsView';

export const dateFilterOptions = ['Daily', 'Past Week', 'Past Month', 'Custom Date'];

const Teams = () => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [newTeamModal, setNewTeamModal] = useState(false);
  const [sortAscending, setSortAscending] = useState(true);
  const [message, setMessage] = useState<MessageType | null>(null);
  const { filterPanel } = useSelector((state: RootState) => state.filterPanel);
  const { fromDateState, toDateState, selectedFilterState } = useSelector((state: RootState) => state.periodSelector);

  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: userList } = useGetUserListQuery({});

  const { user } = useSelector((state: RootState) => state.auth);
  const { data: teams, isFetching } = useGetTeamsQuery(user?.user_id);
  const isAdmin = user.user_role === 'A';

  const filteredTeams = sortAscending
    ? teams &&
      teams
        .filter((team) => team.team_name.toLocaleLowerCase().includes(searchInput))
        .sort((a, b) => (a.team_name > b.team_name ? 1 : -1))
    : teams &&
      teams
        .filter((team) => team.team_name.toLocaleLowerCase().includes(searchInput))
        .sort((a, b) => (a.team_name < b.team_name ? 1 : -1));

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const [dateFilters, setDateFilters] = useState(selectedFilterState || dateFilterOptions[0]);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    fromDateState ? dayjs(fromDateState) : dayjs().subtract(1, 'day'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(toDateState ? dayjs(toDateState) : dayjs());

  // Format dates for the API query
  const formattedStartDate = fromDate ? fromDate.format('YYYY-MM-DD') : '';
  const formattedEndDate = toDate ? toDate.format('YYYY-MM-DD') : '';

  const {
    data: resourceData,
    isFetching: isResourceDataFetching,
    error,
    refetch: refetchResourceData,
  } = useResourceUtilizationQuery(
    {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    },
    {
      // Only run the query when we have both valid dates
      skip: !formattedStartDate || !formattedEndDate,
    },
  );

  // Refetch data when component mounts or when navigating back
  React.useEffect(() => {
    if (formattedStartDate && formattedEndDate) {
      refetchResourceData();
    }
  }, [formattedStartDate, formattedEndDate, refetchResourceData]);

  React.useEffect(() => {
    if (error && 'data' in error) {
      const errData = error.data as { errors?: string } | undefined;
      setMessage({ type: 'error', msg: `${errData?.errors ?? 'An error occurred'}` });
    }
  }, [error]);

  const aside = () => {
    return (
      <>
        {filterPanel && (
          <FilterPanelContainer>
            {!isMobileScreen && (
              <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                Teams
              </Typography>
            )}

            <Box sx={{ paddingY: 2 }}>
              <Tabs tabs={['Overview', 'Teams']} selectedTab={tabIndex} selectTab={setTabIndex} />
            </Box>

            {tabIndex === 0 ? (
              <>
                <PeriodSelector
                  selectedFilter={dateFilters}
                  fromDate={fromDate}
                  toDate={toDate}
                  onFilterChange={setDateFilters}
                  onFromDateChange={setFromDate}
                  onToDateChange={setToDate}
                />
              </>
            ) : (
              <>
                <OutlinedInput
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search Teams"
                  endAdornment={
                    <InputAdornment position="end">
                      {searchInput ? (
                        <IconButton onClick={handleClearSearch} edge="end">
                          <ClearIcon />
                        </IconButton>
                      ) : (
                        <Search />
                      )}
                    </InputAdornment>
                  }
                  sx={{
                    height: '32px',
                    width: '100%',
                  }}
                />
                <FilterButtons sx={{ marginLeft: isAdmin ? 0 : 'auto' }}>
                  <CustomButton
                    onClick={() => setSortAscending(!sortAscending)}
                    text={sortAscending ? 'Descending' : 'Ascending'}
                    variant="contained_grey"
                    endIcon={<SwapVertIcon />}
                    width={isAdmin ? '100%' : 'medium'}
                  />
                  {isAdmin && (
                    <CustomButton
                      width="100%"
                      variant="contained"
                      text="Add Team"
                      onClick={() => {
                        setNewTeamModal(true);
                      }}
                    />
                  )}
                </FilterButtons>
              </>
            )}
          </FilterPanelContainer>
        )}
      </>
    );
  };

  return (
    <>
      {newTeamModal && (
        <AddNewTeam employeeList={userList} onClose={() => setNewTeamModal(false)} setMessage={setMessage} />
      )}
      <PageContainer>
        {aside()}
        {tabIndex === 0 ? (
          <TeamsOverview
            resourceData={error ? [] : resourceData || []}
            isFetching={isResourceDataFetching}
            dateFilters={{ dateFilter: dateFilters, fromDate: fromDate, toDate: toDate }}
          />
        ) : (
          <TeamsView isFetching={isFetching} filteredTeams={filteredTeams || []} searchInput={searchInput} />
        )}
      </PageContainer>
      <SnackBar message={message} onClose={() => setMessage(null)} />
    </>
  );
};

export default Teams;
